import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import useMatrixContext from 'contexts/MatrixContext/useMatrixContext';
import useToast from 'contexts/ToastProvider/useToast';
import { AliasStatusEnum } from 'modules/CreateNewRoomModal/CreateNewRoomDataProvider/utils';
import {
  getIsMatrixError,
  MatrixErrorEnum,
  MAX_ALIAS_RETRY_COUNT,
} from 'modules/CreateNewRoomModal/CreateRoomStepModal/AliasField/utils';
import { getHomeserverFromClient } from 'utils/matrixUtils';

const useWorkspaceAliasAvailability = (alias: string, skipCheck: boolean) => {
  const { t } = useTranslation();
  const { pushToast } = useToast();
  const { matrixClient } = useMatrixContext();
  const [aliasStatus, setAliasStatus] = useState<AliasStatusEnum>(AliasStatusEnum.UNKNOWN);
  const [isAliasCheckLoading, setIsAliasCheckLoading] = useState(false);

  const getAliasAvailability = useCallback(
    async (roomAlias: string, retry = 0) => {
      if (retry > MAX_ALIAS_RETRY_COUNT) {
        pushToast({ variant: 'info', title: t('aliast_not_available'), description: t('use_another_alias') });
        return AliasStatusEnum.UNAVAILABLE;
      }

      try {
        setIsAliasCheckLoading(true);

        const resp = await matrixClient.getRoomIdForAlias(`#${roomAlias}:${getHomeserverFromClient(matrixClient)}`);

        if (resp?.room_id) return AliasStatusEnum.UNAVAILABLE;

        return AliasStatusEnum.AVAILABLE;
      } catch (error) {
        setIsAliasCheckLoading(false);

        if (getIsMatrixError(error) && error.errcode === MatrixErrorEnum.M_NOT_FOUND) {
          return AliasStatusEnum.AVAILABLE;
        }

        return AliasStatusEnum.UNAVAILABLE;
      } finally {
        setIsAliasCheckLoading(false);
      }
    },

    [matrixClient, pushToast, t]
  );

  const checkAlias = useCallback(
    (alias: string) => {
      getAliasAvailability(alias).then((aliasAvailability) => setAliasStatus(aliasAvailability));
    },
    [getAliasAvailability, setAliasStatus]
  );

  useEffect(() => {
    setAliasStatus(AliasStatusEnum.UNKNOWN);
    if (!alias || skipCheck) return;
    const debounceAliasCheck = setTimeout(() => checkAlias(alias), 500);

    return () => {
      clearTimeout(debounceAliasCheck);
    };
  }, [checkAlias, skipCheck, setAliasStatus, alias]);

  return { aliasStatus, isAliasCheckLoading };
};

export default useWorkspaceAliasAvailability;
